import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LiliButtonType } from '@lili/lili-frontend-common';
import LiliToast, { LiliToastDefaultConfig } from '../../../../DesignSystem/LiliToast/LiliToast';
import NoticeModal from '../../../../DesignSystem/NoticeModal/NoticeModal';
import { getBase64Image } from '../../../../helpers/image.helpers';
import { LinkedAccount } from '../../hooks/useLinkedAccounts';
import { useUnlinkAccount } from '../../hooks/useUnlinkAccount';
import { ReactComponent as BankIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-bank-placeholder.svg';

interface UnlinkCardModalProps {
  onFinished: () => void;
  card: LinkedAccount;
  onCanceled?: () => void;
}

const UnlinkCardModalContent = (props: { card: LinkedAccount }) => {
  const { card } = props;
  const { t } = useTranslation();
  return (
    <div className='flex flex-col items-center mb-[61px]'>
      <div className='flex mt-[16px]'>
        <div className='font-metricRSemibold text-[16px] text-bw08'>
          {card.bankName}
          {' '}
          •
          {' '}
          <span className='text-bw07 text-[16px] font-metricR font-normal'>
            {t('TRANSACTIONS_FACELIFT.SYNC_CARD.CREDIT_CARD', { cardNumber: card.cardNumber })}
          </span>
        </div>
      </div>
      <div className='flex mt-[16px] w-[230px] text-center'>
        {t('TRANSACTIONS_FACELIFT.SYNC_CARD.UNLINK_MODAL.TEXT')}
      </div>
    </div>
  );
};

export const UnlinkCardModal = (props: UnlinkCardModalProps) => {
  const { onFinished, card, onCanceled } = props;
  const { mutateAsync: unlinkAccount, isLoading: isUnlinkingAccount } = useUnlinkAccount(card.externalId);
  const { t } = useTranslation();

  const handleUnlink = async () => {
    await unlinkAccount();
    toast(
      <LiliToast
        id='unlink-toast'
        text={t('TRANSACTIONS_FACELIFT.SYNC_CARD.UNLINK_MODAL.SUCCESS', {
          cardNumber: card.cardNumber,
          bank: card.bankName,
        })}
        showCloseButton
      />,
      { ...LiliToastDefaultConfig },
    );
    onFinished();
  };

  return (
    <NoticeModal
      id='unlink-card-modal'
      title={t('TRANSACTIONS_FACELIFT.SYNC_CARD.UNLINK_MODAL.TITLE')}
      content={<UnlinkCardModalContent card={card} />}
      buttonText={t('TRANSACTIONS_FACELIFT.SYNC_CARD.UNLINK_MODAL.UNLINK')}
      secondaryButtonText={t('TRANSACTIONS_FACELIFT.SYNC_CARD.UNLINK_MODAL.CANCEL')}
      onSubmit={handleUnlink}
      image={
        card.institutionLogo ? (
          <div className='rounded-full border-[1px] border-bw04 w-[88px] h-[88px] flex flex-col items-center justify-center'>
            <img src={getBase64Image(card.institutionLogo)} alt='unlink' className='w-[50%] h-[50%]' />
          </div>
        ) : (
          <BankIcon className='w-[88px] h-[88px]' />
        )
      }
      isButtonLoading={false}
      submitButtonProps={{ type: LiliButtonType.DESTRUCTIVE, isLoading: isUnlinkingAccount }}
      onClickOutside={onCanceled}
    />
  );
};
